"use client";
import { cn } from "@/lib/utils";
import { useMotionValue, motion, useMotionTemplate } from "framer-motion";
import React from "react";

export const HeroHighlight = ({ children, className, containerClassName }) => {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    function handleMouseMove({ currentTarget, clientX, clientY }) {
        if (!currentTarget) return;
        const { left, top } = currentTarget.getBoundingClientRect();

        mouseX.set(clientX - left);
        mouseY.set(clientY - top);
    }

    return (
        <div
            className={cn(
                "relative h-[40rem] flex items-center bg-white dark:bg-black justify-center w-full group",
                containerClassName
            )}
            onMouseMove={handleMouseMove}
        >
            <div className="absolute inset-0 bg-dot-thick-neutral-300 dark:bg-dot-thick-neutral-800 pointer-events-none" />
            <motion.div
                className="pointer-events-none bg-dot-thick-indigo-500 dark:bg-dot-thick-indigo-500 absolute inset-0 opacity-0 transition duration-300 group-hover:opacity-100"
                style={{
                    WebkitMaskImage: useMotionTemplate`radial-gradient(200px circle at ${mouseX}px ${mouseY}px, black 0%, transparent 100%)`,
                    maskImage: useMotionTemplate`radial-gradient(200px circle at ${mouseX}px ${mouseY}px, black 0%, transparent 100%)`,
                }}
            />
            <div className={cn("relative z-20", className)}>{children}</div>
        </div>
    );
};

export const Highlight = ({ children, className, variant = "default" }) => {
    const variants = {
        default: "relative inline-block pb-1 px-2 rounded-lg bg-gradient-to-r from-[#2C6E63]/20 via-[#B3BAA8]/30 to-[#2C6E63]/20 text-[#2C6E63] font-semibold dark:from-[#3D8B7A]/30 dark:via-[#B3BAA8]/40 dark:to-[#3D8B7A]/30 dark:text-[#3D8B7A]",
        soft: "relative inline-block pb-1 px-2 rounded-lg bg-gradient-to-r from-[#FAFAF8] via-[#B3BAA8]/20 to-[#FAFAF8] text-[#2C6E63] font-medium border-b-2 border-[#2C6E63]/30",
        nature: "relative inline-block pb-1 px-2 rounded-lg bg-gradient-to-r from-[#B3BAA8]/40 to-[#B3BAA8]/20 text-[#2C3E2D] font-semibold",
        warm: "relative inline-block pb-1 px-2 rounded-lg bg-gradient-to-r from-[#E8ECE4]/60 to-[#2C6E63]/20 text-[#2C6E63] font-semibold",
        bold: "relative inline-block pb-1 px-3 rounded-lg bg-gradient-to-r from-[#2C6E63] to-[#3D8B7A] text-white font-bold shadow-lg"
    };

    return (
        <motion.span
            initial={{
                backgroundSize: "0% 100%",
            }}
            animate={{
                backgroundSize: "100% 100%",
            }}
            transition={{
                duration: 2,
                ease: "linear",
                delay: 0.5,
            }}
            style={{
                backgroundRepeat: "no-repeat",
                backgroundPosition: "left center",
                display: "inline",
            }}
            className={cn(
                variants[variant],
                className
            )}
        >
            {children}
        </motion.span>
    );
}; 